/**
 * Format a date-like value as "MM/DD/YYYY, H:MM AM/PM".
 * Returns `null` if the input is missing or produces an invalid Date.
 * Pass `options` to override individual format fields.
 */
export function formatDateTime(
  value: string | number | Date | null | undefined,
  options?: Intl.DateTimeFormatOptions,
): string | null {
  if (value == null) return null;

  const parsed = value instanceof Date ? value : new Date(value);
  if (isNaN(parsed.getTime())) return null;

  return parsed.toLocaleString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    ...options,
  });
}

/**
 * Format a date-like value as a date-only string (default "MMM D, YYYY").
 * Returns `null` if the input is missing or produces an invalid Date.
 * Delegates to `formatDateTime` with time fields unset.
 */
export function formatDate(
  value: string | number | Date | null | undefined,
  options?: Intl.DateTimeFormatOptions,
): string | null {
  return formatDateTime(value, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: undefined,
    minute: undefined,
    hour12: undefined,
    ...options,
  });
}

/** Compact edit/calendar time for meta lines, e.g. "6/24/26 1:14 PM"; returns
 *  '' when the timestamp is missing or unparseable. Used by the Translation
 *  Review Changes tab and the edit popovers' "Last edited" line (PLAT-10444). */
export function formatEditedAt(at: string | undefined): string {
  return (
    formatDateTime(at, {
      month: 'numeric',
      day: 'numeric',
      year: '2-digit',
    })?.replace(',', '') ?? ''
  );
}

/** Compact calendar time for card/meta lines (Recent Stories cards, clip-card
 *  Published/Saved info): omits the year when it is the current year, e.g.
 *  "Jul 22 at 3:27 PM" vs "Jul 22, 2025 at 3:27 PM". Returns '' when the
 *  timestamp is unparseable. The `" at "` separator can't be produced via Intl
 *  options, so this stays a self-contained date + time split rather than
 *  delegating to `formatDateTime`. */
export function formatDateTimeCompact(at: string | number | Date): string {
  const date = at instanceof Date ? at : new Date(at);
  if (Number.isNaN(date.getTime())) return '';
  const isCurrentYear = date.getFullYear() === new Date().getFullYear();
  return (
    date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      ...(isCurrentYear ? {} : { year: 'numeric' }),
    }) +
    ' at ' +
    date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
  );
}
